// components/admin/AuctionManagement.js
import {
    Activity,
    AlertCircle,
    BarChart3,
    Calendar,
    CheckCircle,
    Clock,
    DollarSign,
    Edit,
    Eye,
    Gavel,
    Plus,
    Search,
    TrendingUp,
    Users,
    XCircle
} from 'lucide-react';
import React, { useEffect, useState } from 'react';
import axios from '../../utils/axiosHandler';

const AuctionManagement = ({ setNotification, setConfirmModal }) => {
    const [activeTab, setActiveTab] = useState('overview');
    const [auctions, setAuctions] = useState([]);
    const [selectedAuction, setSelectedAuction] = useState(null);
    const [bids, setBids] = useState([]);
    const [purchases, setPurchases] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showCreateForm, setShowCreateForm] = useState(false);
    const [showEditForm, setShowEditForm] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState('all');

    // Form state for creating/editing auctions
    const [formData, setFormData] = useState({
        title: '',
        description: '',
        richTextContent: '',
        minimumBid: 8000000,
        maximumBid: 10000000,
        bidIncrement: 150000,
        startDate: '',
        endDate: '',
        status: 'draft',
        adminApprovalRequired: true,
        showDynamicHeader: false,
        headerBackgroundColor: '#1a365d',
        headerTextColor: '#ffffff'
    });

    // Stats state
    const [stats, setStats] = useState({
        totalAuctions: 0,
        activeAuctions: 0,
        totalBids: 0,
        pendingBids: 0,
        totalRevenue: 0,
        pendingApprovals: 0
    });

    const calculateStats = () => {
        const totalAuctions = auctions.length;
        const activeAuctions = auctions.filter(a => a.status === 'active').length;
        const totalBids = bids.filter(b => ['pending', 'active', 'winning', 'approved', 'outbid'].includes(b.status)).length;
        const pendingBids = bids.filter(b => b.status === 'pending').length;

        console.log('Calculating stats:', {
            totalAuctions,
            activeAuctions,
            totalBids,
            pendingBids,
            totalBidsData: bids.length,
            bidsStatuses: bids.map(b => b.status),
            sampleBid: bids.length > 0 ? {
                id: bids[0]._id,
                auctionId: bids[0].auctionId,
                auctionIdType: typeof bids[0].auctionId,
                status: bids[0].status
            } : null
        });

        // Calculate total revenue from approved purchases across all auctions
        const totalRevenue = purchases
            .filter(p => p.status === 'approved')
            .reduce((sum, p) => sum + p.purchaseAmount, 0);

        const pendingApprovals = bids.filter(b => b.status === 'pending').length +
            purchases.filter(p => p.status === 'pending_approval').length;

        setStats({
            totalAuctions,
            activeAuctions,
            totalBids,
            pendingBids,
            totalRevenue,
            pendingApprovals
        });
    };

    // Fetch all bids and purchases for stats calculation
    const fetchAllAuctionData = async () => {
        try {
            // Fetch all bids
            const bidsResponse = await axios.get('/api/auctions/admin/bids');

            // Fetch all purchases
            const purchasesResponse = await axios.get('/api/auctions/admin/purchases');

            if (bidsResponse.status === 200 && purchasesResponse.status === 200) {
                const bidsData = bidsResponse.data;
                const purchasesData = purchasesResponse.data;
                console.log('Fetched bids data:', bidsData.length, 'bids');
                console.log('Fetched purchases data:', purchasesData.length, 'purchases');
                setBids(bidsData);
                setPurchases(purchasesData);
            } else {
                console.error('Failed to fetch auction data:', {
                    bidsStatus: bidsResponse.status,
                    purchasesStatus: purchasesResponse.status
                });
            }
        } catch (error) {
            console.error('Error fetching auction data:', error);
        }
    };

    useEffect(() => {
        fetchAuctions();
        fetchAllAuctionData(); // Fetch all bids and purchases for stats
    }, []);

    useEffect(() => {
        calculateStats();
    }, [auctions, bids, purchases]);

    // Refresh data function
    const refreshData = async () => {
        setLoading(true);
        await Promise.all([
            fetchAuctions(),
            fetchAllAuctionData()
        ]);
        setLoading(false);
    };

    const fetchAuctions = async () => {
        try {
            const response = await axios.get('/api/auctions/admin');

            if (response.status === 200) {
                const data = response.data;
                setAuctions(data);
            }
        } catch (error) {
            console.error('Error fetching auctions:', error);
        } finally {
            setLoading(false);
        }
    };

    const fetchAuctionDetails = async (auctionId) => {
        try {
            // Fetch bids
            const bidsResponse = await axios.get(`/api/auctions/admin/${auctionId}/bids`);

            // Fetch purchases
            const purchasesResponse = await axios.get(`/api/auctions/admin/${auctionId}/purchases`);

            if (bidsResponse.status === 200 && purchasesResponse.status === 200) {
                const bidsData = bidsResponse.data;
                const purchasesData = purchasesResponse.data;
                setBids(bidsData);
                setPurchases(purchasesData);
            }
        } catch (error) {
            console.error('Error fetching auction details:', error);
        }
    };

    const getAuctionBidCount = (auctionId) => {
        const count = bids.filter(bid => {
            const bidAuctionId = typeof bid.auctionId === 'object' ? bid.auctionId._id : bid.auctionId;
            return bidAuctionId === auctionId &&
                ['pending', 'active', 'winning', 'approved', 'outbid'].includes(bid.status);
        }).length;
        console.log(`Bid count for auction ${auctionId}:`, count, 'bids');
        return count;
    };

    const getAuctionPurchaseCount = (auctionId) => {
        return purchases.filter(purchase => {
            const purchaseAuctionId = typeof purchase.auctionId === 'object' ? purchase.auctionId._id : purchase.auctionId;
            return purchaseAuctionId === auctionId;
        }).length;
    };

    const getAuctionPendingBids = (auctionId) => {
        return bids.filter(bid => {
            const bidAuctionId = typeof bid.auctionId === 'object' ? bid.auctionId._id : bid.auctionId;
            return bidAuctionId === auctionId && bid.status === 'pending';
        }).length;
    };

    const getAuctionPendingPurchases = (auctionId) => {
        return purchases.filter(purchase => {
            const purchaseAuctionId = typeof purchase.auctionId === 'object' ? purchase.auctionId._id : purchase.auctionId;
            return purchaseAuctionId === auctionId && purchase.status === 'pending_approval';
        }).length;
    };

    const getAuctionCurrentPrice = (auction) => {
        // If auction is sold, show the maximum bid amount (buy now price)
        if (auction.status === 'sold') {
            return auction.maximumBid;
        }
        // Otherwise show current highest bid
        return auction.currentHighestBid;
    };

    const getAuctionNextMinimumBid = (auction) => {
        if (auction.status === 'sold') {
            return auction.maximumBid; // Already sold at max price
        }
        const currentBid = auction.currentHighestBid || auction.minimumBid;
        return currentBid + auction.bidIncrement;
    };

    const getAuctionTimeRemaining = (auction) => {
        const now = new Date();
        const endDate = new Date(auction.endDate);
        const diff = endDate - now;

        if (diff <= 0) {
            return 'Ended';
        }

        const days = Math.floor(diff / (1000 * 60 * 60 * 24));
        const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));

        if (days > 0) {
            return `${days} days, ${hours} hours`;
        } else {
            return `${hours} hours`;
        }
    };

    const handleCreateAuction = async (e) => {
        e.preventDefault();

        try {
            // Generate the rich text content with embedded buttons
            const richTextContent = generateSimpleAuctionHTML(formData);

            const auctionData = {
                ...formData,
                richTextContent,
                buyNowPrice: formData.maximumBid // Buy now is always the maximum price
            };

            const response = await axios.post('/api/auctions/admin', auctionData);

            if (response.status === 200 || response.status === 201) {
                setNotification({
                    type: 'success',
                    message: 'Auction created successfully!'
                });
                setShowCreateForm(false);
                fetchAuctions();
                resetForm();
            } else {
                const error = await response.json();
                setNotification({
                    type: 'error',
                    message: error.error || 'Failed to create auction'
                });
            }
        } catch (error) {
            console.error('Error creating auction:', error);
            setNotification({
                type: 'error',
                message: 'Error creating auction'
            });
        }
    };

    const handleEditAuction = (auction) => {
        setSelectedAuction(auction._id);
        setFormData({
            title: auction.title,
            description: auction.description,
            richTextContent: auction.richTextContent,
            minimumBid: auction.minimumBid,
            maximumBid: auction.maximumBid,
            bidIncrement: auction.bidIncrement,
            startDate: new Date(auction.startDate).toISOString().slice(0, 16),
            endDate: new Date(auction.endDate).toISOString().slice(0, 16),
            status: auction.status,
            adminApprovalRequired: auction.adminApprovalRequired,
            showDynamicHeader: false, // Reset dynamic header state
            headerBackgroundColor: '#1a365d', // Reset header colors
            headerTextColor: '#ffffff'
        });
        setShowEditForm(true);
    };

    const handleUpdateAuction = async (e) => {
        e.preventDefault();

        try {
            const token = localStorage.getItem('token');

            // If richTextContent is provided, ensure it has the required JavaScript functionality
            let updatedFormData = { ...formData };

            if (formData.richTextContent && !formData.richTextContent.includes('handleBidClick')) {
                // Extract the content from the richTextContent and wrap it with proper JavaScript
                const contentMatch = formData.richTextContent.match(/<body[^>]*>([\s\S]*)<\/body>/i);
                if (contentMatch) {
                    const bodyContent = contentMatch[1];
                    // Remove any existing script tags from the body content
                    const cleanContent = bodyContent.replace(/<script[\s\S]*?<\/script>/gi, '');

                    // Generate the complete HTML with JavaScript functionality
                    const completeHTML = generateSimpleAuctionHTML({
                        ...formData,
                        _id: selectedAuction,
                        richTextContent: cleanContent
                    });

                    updatedFormData.richTextContent = completeHTML;
                }
            }

            const response = await axios.put(`/api/auctions/admin/${selectedAuction}`, updatedFormData);

            if (response.status === 200 || response.status === 201) {
                setNotification({
                    type: 'success',
                    message: 'Auction updated successfully!'
                });
                setShowEditForm(false);
                setSelectedAuction(null);
                fetchAuctions();
                resetForm();
            } else {
                setNotification({
                    type: 'error',
                    message: response.data?.error || 'Failed to update auction'
                });
            }
        } catch (error) {
            console.error('Error updating auction:', error);
            setNotification({
                type: 'error',
                message: 'Error updating auction'
            });
        }
    };

    const handleActivateAuction = async (auctionId) => {
        try {
            // Get the current auction to check its dates
            const auctionResponse = await axios.get(`/api/auctions/admin/${auctionId}`);

            if (auctionResponse.status !== 200) {
                throw new Error('Failed to fetch auction details');
            }

            const auction = auctionResponse.data;

            // Set proper dates if they're not already set
            const now = new Date();
            const startDate = auction.startDate ? new Date(auction.startDate) : now;
            const endDate = auction.endDate ? new Date(auction.endDate) : new Date(now.getTime() + (30 * 24 * 60 * 60 * 1000)); // 30 days from now

            // If start date is in the future, set it to now
            if (startDate > now) {
                startDate.setTime(now.getTime());
            }

            // If end date is before start date, set it to 30 days from start
            if (endDate <= startDate) {
                endDate.setTime(startDate.getTime() + (30 * 24 * 60 * 60 * 1000));
            }

            const response = await axios.put(`/api/auctions/admin/${auctionId}`, {
                status: 'active',
                startDate: startDate.toISOString(),
                endDate: endDate.toISOString()
            });

            if (response.status === 200 || response.status === 201) {
                setNotification({
                    type: 'success',
                    message: 'Auction activated successfully with proper dates!'
                });
                fetchAuctions();
            } else {
                setNotification({
                    type: 'error',
                    message: response.data?.error || 'Failed to activate auction'
                });
            }
        } catch (error) {
            console.error('Error activating auction:', error);
            setNotification({
                type: 'error',
                message: 'Error activating auction'
            });
        }
    };

    const handleDeleteAuction = async (auctionId) => {
        if (window.confirm('Are you sure you want to delete this auction? This action cannot be undone.')) {
            try {
                const response = await axios.delete(`/api/auctions/admin/${auctionId}`);

                if (response.status === 200 || response.status === 204) {
                    setNotification({
                        type: 'success',
                        message: 'Auction deleted successfully!'
                    });
                    fetchAuctions();
                } else {
                    setNotification({
                        type: 'error',
                        message: response.data?.error || 'Failed to delete auction'
                    });
                }
            } catch (error) {
                console.error('Error deleting auction:', error);
                setNotification({
                    type: 'error',
                    message: 'Error deleting auction'
                });
            }
        }
    };

    const handleBidApproval = async (bidId, approve, notes = '') => {
        try {
            const response = await axios.post(`/api/auctions/admin/bids/${bidId}/approve`, { approve, notes });

            if (response.status === 200 || response.status === 201) {
                setNotification({
                    type: 'success',
                    message: `Bid ${approve ? 'approved' : 'rejected'} successfully!`
                });
                fetchAuctionDetails(selectedAuction);
                fetchAuctions(); // Refresh auction list
            }
        } catch (error) {
            console.error('Error processing bid:', error);
            setNotification({
                type: 'error',
                message: 'Error processing bid approval'
            });
        }
    };

    const handlePurchaseApproval = async (purchaseId, approve, notes = '') => {
        try {
            const response = await axios.post(`/api/auctions/admin/purchases/${purchaseId}/approve`, { approve, notes });

            if (response.status === 200 || response.status === 201) {
                setNotification({
                    type: 'success',
                    message: `Purchase ${approve ? 'approved' : 'rejected'} successfully!`
                });
                fetchAuctionDetails(selectedAuction);
                fetchAuctions(); // Refresh auction list
            }
        } catch (error) {
            console.error('Error processing purchase:', error);
            setNotification({
                type: 'error',
                message: 'Error processing purchase approval'
            });
        }
    };

    const resetForm = () => {
        setFormData({
            title: '',
            description: '',
            richTextContent: '',
            minimumBid: 8000000,
            maximumBid: 10000000,
            bidIncrement: 150000,
            startDate: '',
            endDate: '',
            status: 'draft',
            adminApprovalRequired: true,
            showDynamicHeader: false,
            headerBackgroundColor: '#1a365d',
            headerTextColor: '#ffffff'
        });
    };

    const generateDynamicHeaderHTML = (auction) => {
        if (!auction) {
            // Use form data for preview during creation
            const currentPrice = formData.minimumBid;
            const nextBid = currentPrice + formData.bidIncrement;
            const bidCount = 0;
            const timeRemaining = 'Calculating...';

            return `
                <div style="background: ${formData.headerBackgroundColor}; color: ${formData.headerTextColor}; padding: 20px; border-radius: 10px; margin-bottom: 20px; text-align: center;">
                    <div style="display: grid; grid-template-columns: repeat(auto-fit, minmax(200px, 1fr)); gap: 20px; max-width: 800px; margin: 0 auto;">
                        <div>
                            <div style="font-size: 14px; opacity: 0.8; margin-bottom: 5px;">Current Highest Bid</div>
                            <div style="font-size: 24px; font-weight: bold;">$${currentPrice.toLocaleString()}</div>
                        </div>
                        <div>
                            <div style="font-size: 14px; opacity: 0.8; margin-bottom: 5px;">Next Minimum Bid</div>
                            <div style="font-size: 24px; font-weight: bold;">$${nextBid.toLocaleString()}</div>
                        </div>
                        <div>
                            <div style="font-size: 14px; opacity: 0.8; margin-bottom: 5px;">Total Bids</div>
                            <div style="font-size: 24px; font-weight: bold;">${bidCount}</div>
                        </div>
                        <div>
                            <div style="font-size: 14px; opacity: 0.8; margin-bottom: 5px;">Time Remaining</div>
                            <div style="font-size: 24px; font-weight: bold;">${timeRemaining}</div>
                        </div>
                    </div>
                </div>
            `;
        }

        const currentPrice = getAuctionCurrentPrice(auction);
        const nextBid = getAuctionNextMinimumBid(auction);
        const bidCount = getAuctionBidCount(auction._id);
        const timeRemaining = getAuctionTimeRemaining(auction);

        return `
            <div style="background: ${formData.headerBackgroundColor}; color: ${formData.headerTextColor}; padding: 20px; border-radius: 10px; margin-bottom: 20px; text-align: center;">
                <div style="display: grid; grid-template-columns: repeat(auto-fit, minmax(200px, 1fr)); gap: 20px; max-width: 800px; margin: 0 auto;">
                    <div>
                        <div style="font-size: 14px; opacity: 0.8; margin-bottom: 5px;">Current Highest Bid</div>
                        <div style="font-size: 24px; font-weight: bold;">$${currentPrice.toLocaleString()}</div>
                    </div>
                    <div>
                        <div style="font-size: 14px; opacity: 0.8; margin-bottom: 5px;">Next Minimum Bid</div>
                        <div style="font-size: 24px; font-weight: bold;">$${nextBid.toLocaleString()}</div>
                    </div>
                    <div>
                        <div style="font-size: 14px; opacity: 0.8; margin-bottom: 5px;">Total Bids</div>
                        <div style="font-size: 24px; font-weight: bold;">${bidCount}</div>
                    </div>
                    <div>
                        <div style="font-size: 14px; opacity: 0.8; margin-bottom: 5px;">Time Remaining</div>
                        <div style="font-size: 24px; font-weight: bold;">${timeRemaining}</div>
                    </div>
                </div>
            </div>
        `;
    };

    // Generate the Pahlavi auction HTML with embedded buttons
    const generateSimpleAuctionHTML = (auction) => {
        // Create a mock auction object for preview during creation
        const previewAuction = auction || {
            _id: 'PREVIEW_ID',
            title: formData.title || 'Auction Title',
            description: formData.description || 'Auction Description',
            minimumBid: formData.minimumBid,
            maximumBid: formData.maximumBid,
            bidIncrement: formData.bidIncrement,
            currentHighestBid: formData.minimumBid,
            status: formData.status
        };

        // Check if we have custom richTextContent to use
        let customContent = '';
        if (auction && auction.richTextContent) {
            // Extract content from existing richTextContent
            const bodyMatch = auction.richTextContent.match(/<body[^>]*>([\s\S]*)<\/body>/i);
            if (bodyMatch) {
                customContent = bodyMatch[1];
                // Remove any existing script tags and dynamic header
                customContent = customContent.replace(/<script[\s\S]*?<\/script>/gi, '');
                customContent = customContent.replace(/<div class="auction-header"[\s\S]*?<\/div>/gi, '');
            }
        }

        // If no custom content, use simple default template
        if (!customContent) {
            customContent = `
                <div class="auction-content">
                    <h1>${previewAuction.title}</h1>
                    <p>${previewAuction.description}</p>
                    
                    <!-- IMPORTANT: Keep these button IDs for functionality -->
                    <button id="auction-bid-btn" class="auction-btn bid-btn">BID</button>
                    <button id="auction-buynow-btn" class="auction-btn buynow-btn">BUY NOW</button>
                </div>
            `;
        }

        return `<!DOCTYPE html>
<html lang="en">
<head>
    <meta charset="UTF-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <title>${previewAuction.title}</title>
    <style>
        /* Basic auction styles - you can customize these */
        body {
            font-family: Arial, sans-serif;
            margin: 0;
            padding: 20px;
            background-color: #f5f5f5;
        }
        
        .auction-header {
            background: ${formData.headerBackgroundColor || '#1a365d'};
            color: ${formData.headerTextColor || '#ffffff'};
            padding: 20px;
            border-radius: 10px;
            margin-bottom: 20px;
            text-align: center;
        }
        
        .auction-stats {
            display: grid;
            grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
            gap: 20px;
            max-width: 800px;
            margin: 0 auto;
        }
        
        .stat-item {
            text-align: center;
        }
        
        .stat-label {
            font-size: 14px;
            opacity: 0.8;
            margin-bottom: 5px;
        }
        
        .stat-value {
            font-size: 24px;
            font-weight: bold;
        }
        
        .auction-btn {
            padding: 15px 30px;
            margin: 10px;
            border: none;
            border-radius: 8px;
            font-size: 16px;
            font-weight: bold;
            cursor: pointer;
            transition: all 0.3s ease;
        }
        
        .bid-btn {
            background: linear-gradient(135deg, #1e3a5f 0%, #2c5282 100%);
            color: white;
        }
        
        .bid-btn:hover {
            transform: translateY(-2px);
            box-shadow: 0 4px 12px rgba(30, 58, 95, 0.3);
        }
        
        .buynow-btn {
            background: linear-gradient(135deg, #e8e8e8 0%, #d0d0d0 100%);
            color: #333;
        }
        
        .buynow-btn:hover {
            transform: translateY(-2px);
            box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
        }
        
        .auction-content {
            background: white;
            padding: 30px;
            border-radius: 10px;
            box-shadow: 0 2px 10px rgba(0,0,0,0.1);
            margin-bottom: 20px;
        }
        
        .auction-content h1 {
            color: #1a365d;
            margin-bottom: 20px;
        }
        
        .auction-content p {
            line-height: 1.6;
            color: #4a5568;
            margin-bottom: 20px;
        }
    </style>
</head>
<body>
    ${formData.showDynamicHeader ? generateDynamicHeaderHTML(previewAuction) : ''}
    
    ${customContent}

    <script>
        // Initialize auction functionality
        document.addEventListener('DOMContentLoaded', function() {
            const auctionId = '${previewAuction._id}';

            // Bind all bid buttons
            document.querySelectorAll('[id^="auction-bid-btn"]').forEach(btn => {
                btn.addEventListener('click', () => handleBidClick(auctionId));
            });

            // Bind all buy now buttons
            document.querySelectorAll('[id^="auction-buynow-btn"]').forEach(btn => {
                btn.addEventListener('click', () => handleBuyNowClick(auctionId));
            });

            // Load current auction status
            loadAuctionStatus(auctionId);

            // Update every 30 seconds
            setInterval(() => loadAuctionStatus(auctionId), 30000);
        });

        async function handleBidClick(auctionId) {
            if (window.showBidModal) {
                window.showBidModal(auctionId);
            } else {
                window.location.href = \`/auction/\${auctionId}\`;
            }
        }

        async function handleBuyNowClick(auctionId) {
            if (window.showBuyNowModal) {
                window.showBuyNowModal(auctionId);
            } else {
                window.location.href = \`/auction/\${auctionId}\`;
            }
        }

        async function loadAuctionStatus(auctionId) {
            try {
                const response = await fetch(\`/api/auctions/public/\${auctionId}\`);
                const auction = await response.json();
                
                // Update dynamic header if it exists
                const header = document.querySelector('.auction-header');
                if (header) {
                    header.innerHTML = generateHeaderContent(auction);
                }
            } catch (error) {
                console.error('Error loading auction status:', error);
            }
        }

        function generateHeaderContent(auction) {
            const currentPrice = auction.currentHighestBid || auction.minimumBid;
            const nextBid = currentPrice + auction.bidIncrement;
            const timeRemaining = auction.timeRemaining || 'Calculating...';
            
            return \`
                <div class="auction-stats">
                    <div class="stat-item">
                        <div class="stat-label">Current Highest Bid</div>
                        <div class="stat-value">\$\${currentPrice.toLocaleString()}</div>
                    </div>
                    <div class="stat-item">
                        <div class="stat-label">Next Minimum Bid</div>
                        <div class="stat-value">\$\${nextBid.toLocaleString()}</div>
                    </div>
                    <div class="stat-item">
                        <div class="stat-label">Total Bids</div>
                        <div class="stat-value">\${auction.totalBids || 0}</div>
                    </div>
                    <div class="stat-item">
                        <div class="stat-label">Time Remaining</div>
                        <div class="stat-value">\${timeRemaining}</div>
                    </div>
                </div>
            \`;
        }
    </script>
</body>
</html>`;
    };

    const loadSimpleTemplate = () => {
        const template = generateSimpleAuctionHTML(formData);
        setFormData({ ...formData, richTextContent: template });
    };

    const renderAuctionsTab = () => {
        const filteredAuctions = auctions.filter(auction => {
            const matchesSearch = auction.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                auction.description.toLowerCase().includes(searchTerm.toLowerCase());
            const matchesStatus = statusFilter === 'all' || auction.status === statusFilter;
            return matchesSearch && matchesStatus;
        });

        return (
            <div className="space-y-6">
                {/* Header */}
                <div className="flex justify-between items-center">
                    <div>
                        <h3 className="text-2xl font-bold text-gray-900">All Auctions</h3>
                        <p className="text-gray-600 mt-1">Manage your auction inventory</p>
                    </div>
                    <div className="flex gap-3">
                        <button
                            onClick={refreshData}
                            className="bg-gray-600 text-white px-4 py-3 rounded-xl hover:bg-gray-700 transition-all duration-200 flex items-center gap-2 shadow-lg hover:shadow-xl"
                        >
                            <Activity className="w-5 h-5" />
                            Refresh
                        </button>
                        <button
                            onClick={() => setShowCreateForm(true)}
                            className="bg-gradient-to-r from-blue-600 to-blue-700 text-white px-6 py-3 rounded-xl hover:from-blue-700 hover:to-blue-800 transition-all duration-200 flex items-center gap-2 shadow-lg hover:shadow-xl"
                        >
                            <Plus className="w-5 h-5" />
                            Create New Auction
                        </button>
                    </div>
                </div>

                {/* Search and Filter */}
                <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-6">
                    <div className="flex flex-col md:flex-row gap-4">
                        <div className="flex-1">
                            <div className="relative">
                                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                                <input
                                    type="text"
                                    placeholder="Search auctions..."
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    className="w-full pl-10 pr-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                />
                            </div>
                        </div>
                        <div className="flex gap-2">
                            <select
                                value={statusFilter}
                                onChange={(e) => setStatusFilter(e.target.value)}
                                className="px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            >
                                <option value="all">All Status</option>
                                <option value="draft">Draft</option>
                                <option value="active">Active</option>
                                <option value="ended">Ended</option>
                                <option value="sold">Sold</option>
                            </select>
                        </div>
                    </div>
                </div>

                {/* Auctions Grid */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    {loading ? (
                        <div className="col-span-full flex justify-center py-12">
                            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
                        </div>
                    ) : filteredAuctions.length === 0 ? (
                        <div className="col-span-full text-center py-12">
                            <Gavel className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                            <h3 className="text-lg font-medium text-gray-900 mb-2">No auctions found</h3>
                            <p className="text-gray-500 mb-4">
                                {searchTerm || statusFilter !== 'all'
                                    ? 'Try adjusting your search or filter criteria'
                                    : 'Create your first auction to get started'
                                }
                            </p>
                            {!searchTerm && statusFilter === 'all' && (
                                <button
                                    onClick={() => setShowCreateForm(true)}
                                    className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700"
                                >
                                    Create Auction
                                </button>
                            )}
                        </div>
                    ) : (
                        filteredAuctions.map(auction => (
                            <div key={auction._id} className="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden hover:shadow-xl transition-shadow">
                                <div className="p-6">
                                    <div className="flex justify-between items-start mb-4">
                                        <div className="flex-1">
                                            <div className="flex items-center gap-3 mb-2">
                                                <span className={`px-3 py-1 rounded-full text-xs font-medium ${auction.status === 'active' ? 'bg-green-100 text-green-800' :
                                                    auction.status === 'draft' ? 'bg-yellow-100 text-yellow-800' :
                                                        auction.status === 'ended' ? 'bg-red-100 text-red-800' :
                                                            auction.status === 'sold' ? 'bg-blue-100 text-blue-800' :
                                                                'bg-gray-100 text-gray-800'
                                                    }`}>
                                                    {auction.status.toUpperCase()}
                                                </span>
                                                {getAuctionPendingBids(auction._id) > 0 && (
                                                    <span className="px-2 py-1 bg-orange-100 text-orange-800 rounded-full text-xs font-medium">
                                                        {getAuctionPendingBids(auction._id)} pending bids
                                                    </span>
                                                )}
                                                {getAuctionPendingPurchases(auction._id) > 0 && (
                                                    <span className="px-2 py-1 bg-red-100 text-red-800 rounded-full text-xs font-medium">
                                                        {getAuctionPendingPurchases(auction._id)} pending purchases
                                                    </span>
                                                )}
                                            </div>
                                            <h4 className="text-xl font-bold text-gray-900 mb-2">{auction.title}</h4>
                                            <p className="text-gray-600 mb-4 line-clamp-2">{auction.description}</p>
                                        </div>
                                    </div>

                                    <div className="grid grid-cols-2 gap-4 mb-6">
                                        <div className="text-center p-4 bg-blue-50 rounded-xl">
                                            <DollarSign className="w-6 h-6 mx-auto text-blue-600 mb-2" />
                                            <div className="text-sm text-gray-600">Min Bid</div>
                                            <div className="font-bold text-blue-600">${auction.minimumBid.toLocaleString()}</div>
                                        </div>
                                        <div className="text-center p-4 bg-green-50 rounded-xl">
                                            <TrendingUp className="w-6 h-6 mx-auto text-green-600 mb-2" />
                                            <div className="text-sm text-gray-600">Buy Now</div>
                                            <div className="font-bold text-green-600">${auction.maximumBid.toLocaleString()}</div>
                                        </div>
                                    </div>

                                    <div className="flex items-center justify-between text-sm text-gray-600 mb-6">
                                        <span className="flex items-center gap-1">
                                            <Users className="w-4 h-4" />
                                            {getAuctionBidCount(auction._id)} bids
                                            {getAuctionPendingBids(auction._id) > 0 && (
                                                <span className="text-yellow-600 font-medium">
                                                    ({getAuctionPendingBids(auction._id)} pending)
                                                </span>
                                            )}
                                        </span>
                                        <span className="flex items-center gap-1">
                                            <DollarSign className="w-4 h-4" />
                                            Current: ${getAuctionCurrentPrice(auction).toLocaleString()}
                                        </span>
                                        <span className="flex items-center gap-1">
                                            <Calendar className="w-4 h-4" />
                                            {new Date(auction.endDate).toLocaleDateString()}
                                        </span>
                                    </div>

                                    <div className="flex gap-2">
                                        <button
                                            onClick={() => {
                                                setSelectedAuction(auction._id);
                                                setActiveTab('details');
                                                fetchAuctionDetails(auction._id);
                                            }}
                                            className="flex-1 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
                                        >
                                            <Eye className="w-4 h-4 inline mr-2" />
                                            View Details
                                        </button>
                                        <button
                                            onClick={() => handleEditAuction(auction)}
                                            className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
                                        >
                                            <Edit className="w-4 h-4" />
                                        </button>
                                        <button
                                            onClick={() => window.open(`/auction/${auction._id}`, '_blank')}
                                            className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
                                        >
                                            <Eye className="w-4 h-4" />
                                        </button>
                                        {auction.status === 'draft' && (
                                            <button
                                                onClick={() => handleActivateAuction(auction._id)}
                                                className="px-4 py-2 bg-green-100 text-green-700 rounded-lg hover:bg-green-200 transition-colors"
                                                title="Activate Auction"
                                            >
                                                <CheckCircle className="w-4 h-4" />
                                            </button>
                                        )}
                                        <button
                                            onClick={() => handleDeleteAuction(auction._id)}
                                            className="px-4 py-2 bg-red-100 text-red-700 rounded-lg hover:bg-red-200 transition-colors"
                                        >
                                            <XCircle className="w-4 h-4" />
                                        </button>
                                    </div>
                                </div>
                            </div>
                        ))
                    )}
                </div>
            </div>
        );
    };

    const renderDetailsTab = () => {
        const auction = auctions.find(a => a._id === selectedAuction);
        if (!auction) return <div>No auction selected</div>;

        return (
            <div className="space-y-6">
                {/* Header */}
                <div className="flex items-center gap-4">
                    <button
                        onClick={() => setActiveTab('auctions')}
                        className="text-blue-600 hover:text-blue-800"
                    >
                        ← Back to Auctions
                    </button>
                    <h3 className="text-2xl font-bold text-gray-900">{auction.title}</h3>
                </div>

                {/* Auction Summary */}
                <div className="bg-white rounded-lg shadow border p-6">
                    <h4 className="text-lg font-semibold mb-4">Auction Summary</h4>
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                        <div className="text-center p-4 bg-blue-50 rounded-lg">
                            <DollarSign className="w-8 h-8 mx-auto text-blue-600 mb-2" />
                            <div className="text-sm text-gray-600">Current Highest Bid</div>
                            <div className="text-xl font-bold text-blue-600">
                                {getAuctionCurrentPrice(auction) > 0 ? `${getAuctionCurrentPrice(auction).toLocaleString()}` : 'No bids'}
                            </div>
                        </div>
                        <div className="text-center p-4 bg-green-50 rounded-lg">
                            <Users className="w-8 h-8 mx-auto text-green-600 mb-2" />
                            <div className="text-sm text-gray-600">Total Bids</div>
                            <div className="text-xl font-bold text-green-600">{getAuctionBidCount(auction._id)}</div>
                            {getAuctionPendingBids(auction._id) > 0 && (
                                <div className="text-sm text-yellow-600 font-medium">
                                    {getAuctionPendingBids(auction._id)} pending
                                </div>
                            )}
                        </div>
                        <div className="text-center p-4 bg-purple-50 rounded-lg">
                            <BarChart3 className="w-8 h-8 mx-auto text-purple-600 mb-2" />
                            <div className="text-sm text-gray-600">Buy Now Requests</div>
                            <div className="text-xl font-bold text-purple-600">{getAuctionPurchaseCount(auction._id)}</div>
                        </div>
                        <div className="text-center p-4 bg-orange-50 rounded-lg">
                            <Clock className="w-8 h-8 mx-auto text-orange-600 mb-2" />
                            <div className="text-sm text-gray-600">Status</div>
                            <div className={`text-xl font-bold ${auction.status === 'active' ? 'text-green-600' :
                                auction.status === 'draft' ? 'text-yellow-600' :
                                    auction.status === 'ended' ? 'text-red-600' :
                                        'text-gray-600'
                                }`}>
                                {auction.status.toUpperCase()}
                            </div>
                        </div>
                    </div>
                </div>

                {/* Bids Section */}
                <div className="bg-white rounded-lg shadow border">
                    <div className="p-6 border-b">
                        <h4 className="text-lg font-semibold flex items-center gap-2">
                            <Gavel className="w-5 h-5" />
                            Auction Bids ({bids.length})
                        </h4>
                    </div>
                    <div className="p-6">
                        {bids.length === 0 ? (
                            <div className="text-center py-8 text-gray-500">
                                No bids submitted yet.
                            </div>
                        ) : (
                            <div className="space-y-4">
                                {bids.map(bid => (
                                    <div key={bid._id} className="border rounded-lg p-4">
                                        <div className="flex justify-between items-start">
                                            <div className="flex-1">
                                                <div className="flex items-center gap-4 mb-2">
                                                    <h5 className="font-semibold text-gray-900">
                                                        {bid.userId.firstName} {bid.userId.lastName}
                                                    </h5>
                                                    <span className={`px-2 py-1 rounded text-xs font-medium ${bid.status === 'approved' ? 'bg-green-100 text-green-800' :
                                                        bid.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                                                            bid.status === 'winning' ? 'bg-blue-100 text-blue-800' :
                                                                bid.status === 'rejected' ? 'bg-red-100 text-red-800' :
                                                                    'bg-gray-100 text-gray-800'
                                                        }`}>
                                                        {bid.status.toUpperCase()}
                                                    </span>
                                                </div>
                                                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                                                    <div>
                                                        <span className="text-gray-600">Bid Amount:</span>
                                                        <div className="font-semibold text-lg text-blue-600">
                                                            ${bid.bidAmount.toLocaleString()}
                                                        </div>
                                                    </div>
                                                    <div>
                                                        <span className="text-gray-600">Email:</span>
                                                        <div className="font-medium">{bid.userId.email}</div>
                                                    </div>
                                                    <div>
                                                        <span className="text-gray-600">Phone:</span>
                                                        <div className="font-medium">{bid.userId.phone || 'N/A'}</div>
                                                    </div>
                                                    <div>
                                                        <span className="text-gray-600">Submitted:</span>
                                                        <div className="font-medium">
                                                            {new Date(bid.createdAt).toLocaleDateString()}
                                                        </div>
                                                    </div>
                                                </div>
                                                {bid.adminNotes && (
                                                    <div className="mt-2 p-2 bg-gray-50 rounded text-sm">
                                                        <span className="font-medium">Admin Notes:</span> {bid.adminNotes}
                                                    </div>
                                                )}
                                            </div>

                                            {bid.status === 'pending' && (
                                                <div className="flex gap-2 ml-4">
                                                    <button
                                                        onClick={() => {
                                                            const notes = prompt('Add notes (optional):');
                                                            handleBidApproval(bid._id, true, notes || '');
                                                        }}
                                                        className="bg-green-600 text-white px-3 py-1 rounded text-sm hover:bg-green-700 flex items-center gap-1"
                                                    >
                                                        <CheckCircle className="w-4 h-4" />
                                                        Approve
                                                    </button>
                                                    <button
                                                        onClick={() => {
                                                            const notes = prompt('Rejection reason (optional):');
                                                            handleBidApproval(bid._id, false, notes || '');
                                                        }}
                                                        className="bg-red-600 text-white px-3 py-1 rounded text-sm hover:bg-red-700 flex items-center gap-1"
                                                    >
                                                        <XCircle className="w-4 h-4" />
                                                        Reject
                                                    </button>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>

                {/* Buy Now Requests Section */}
                <div className="bg-white rounded-lg shadow border">
                    <div className="p-6 border-b">
                        <h4 className="text-lg font-semibold flex items-center gap-2">
                            <DollarSign className="w-5 h-5" />
                            Buy Now Requests ({purchases.length})
                        </h4>
                    </div>
                    <div className="p-6">
                        {purchases.length === 0 ? (
                            <div className="text-center py-8 text-gray-500">
                                No buy now requests submitted yet.
                            </div>
                        ) : (
                            <div className="space-y-4">
                                {purchases.map(purchase => (
                                    <div key={purchase._id} className="border rounded-lg p-4 bg-green-50">
                                        <div className="flex justify-between items-start">
                                            <div className="flex-1">
                                                <div className="flex items-center gap-4 mb-2">
                                                    <h5 className="font-semibold text-gray-900">
                                                        {purchase.userId.firstName} {purchase.userId.lastName}
                                                    </h5>
                                                    <span className={`px-2 py-1 rounded text-xs font-medium ${purchase.status === 'approved' ? 'bg-green-100 text-green-800' :
                                                        purchase.status === 'pending_approval' ? 'bg-yellow-100 text-yellow-800' :
                                                            purchase.status === 'rejected' ? 'bg-red-100 text-red-800' :
                                                                purchase.status === 'completed' ? 'bg-blue-100 text-blue-800' :
                                                                    'bg-gray-100 text-gray-800'
                                                        }`}>
                                                        {purchase.status.replace('_', ' ').toUpperCase()}
                                                    </span>
                                                    {purchase.status === 'pending_approval' && (
                                                        <span className="bg-red-500 text-white px-2 py-1 rounded text-xs animate-pulse">
                                                            URGENT - REQUIRES IMMEDIATE ACTION
                                                        </span>
                                                    )}
                                                </div>
                                                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                                                    <div>
                                                        <span className="text-gray-600">Purchase Amount:</span>
                                                        <div className="font-semibold text-lg text-green-600">
                                                            ${purchase?.purchaseAmount.toLocaleString()}
                                                        </div>
                                                    </div>
                                                    <div>
                                                        <span className="text-gray-600">Email:</span>
                                                        <div className="font-medium">{purchase.userId.email}</div>
                                                    </div>
                                                    <div>
                                                        <span className="text-gray-600">Phone:</span>
                                                        <div className="font-medium">{purchase.userId.phone || 'N/A'}</div>
                                                    </div>
                                                    <div>
                                                        <span className="text-gray-600">Requested:</span>
                                                        <div className="font-medium">
                                                            {new Date(purchase.createdAt).toLocaleDateString()}
                                                        </div>
                                                    </div>
                                                </div>
                                                {purchase.adminNotes && (
                                                    <div className="mt-2 p-2 bg-gray-50 rounded text-sm">
                                                        <span className="font-medium">Admin Notes:</span> {purchase.adminNotes}
                                                    </div>
                                                )}
                                            </div>

                                            {purchase.status === 'pending_approval' && (
                                                <div className="flex gap-2 ml-4">
                                                    <button
                                                        onClick={() => {
                                                            const notes = prompt('Add notes (optional):');
                                                            handlePurchaseApproval(purchase._id, true, notes || '');
                                                        }}
                                                        className="bg-green-600 text-white px-3 py-1 rounded text-sm hover:bg-green-700 flex items-center gap-1"
                                                    >
                                                        <CheckCircle className="w-4 h-4" />
                                                        Approve Purchase
                                                    </button>
                                                    <button
                                                        onClick={() => {
                                                            const notes = prompt('Rejection reason (optional):');
                                                            handlePurchaseApproval(purchase._id, false, notes || '');
                                                        }}
                                                        className="bg-red-600 text-white px-3 py-1 rounded text-sm hover:bg-red-700 flex items-center gap-1"
                                                    >
                                                        <XCircle className="w-4 h-4" />
                                                        Reject
                                                    </button>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            </div>
        );
    };

    const renderOverviewTab = () => (
        <div className="space-y-8">
            {/* Header */}
            <div className="flex justify-between items-center">
                <div>
                    <h3 className="text-3xl font-bold text-gray-900">Auction Dashboard</h3>
                    <p className="text-gray-600 mt-1">Manage and monitor your auction activities</p>
                </div>
                <div className="flex gap-3">
                    <button
                        onClick={refreshData}
                        className="bg-gray-600 text-white px-4 py-3 rounded-xl hover:bg-gray-700 transition-all duration-200 flex items-center gap-2 shadow-lg hover:shadow-xl"
                    >
                        <Activity className="w-5 h-5" />
                        Refresh
                    </button>
                    <button
                        onClick={() => setShowCreateForm(true)}
                        className="bg-gradient-to-r from-blue-600 to-blue-700 text-white px-6 py-3 rounded-xl hover:from-blue-700 hover:to-blue-800 transition-all duration-200 flex items-center gap-2 shadow-lg hover:shadow-xl"
                    >
                        <Plus className="w-5 h-5" />
                        Create Auction
                    </button>
                </div>
            </div>

            {/* Stats Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-6">
                <div className="bg-gradient-to-br from-blue-500 to-blue-600 text-white p-6 rounded-2xl shadow-lg">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-blue-100 text-sm font-medium">Total Auctions</p>
                            <p className="text-3xl font-bold">{stats.totalAuctions}</p>
                        </div>
                        <Gavel className="w-8 h-8 text-blue-200" />
                    </div>
                </div>

                <div className="bg-gradient-to-br from-green-500 to-green-600 text-white p-6 rounded-2xl shadow-lg">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-green-100 text-sm font-medium">Active Auctions</p>
                            <p className="text-3xl font-bold">{stats.activeAuctions}</p>
                        </div>
                        <Activity className="w-8 h-8 text-green-200" />
                    </div>
                </div>

                <div className="bg-gradient-to-br from-purple-500 to-purple-600 text-white p-6 rounded-2xl shadow-lg">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-purple-100 text-sm font-medium">Total Bids</p>
                            <p className="text-3xl font-bold">{stats.totalBids}</p>
                        </div>
                        <TrendingUp className="w-8 h-8 text-purple-200" />
                    </div>
                </div>

                <div className="bg-gradient-to-br from-yellow-500 to-yellow-600 text-white p-6 rounded-2xl shadow-lg">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-yellow-100 text-sm font-medium">Pending Bids</p>
                            <p className="text-3xl font-bold">{stats.pendingBids}</p>
                        </div>
                        <Clock className="w-8 h-8 text-yellow-200" />
                    </div>
                </div>

                <div className="bg-gradient-to-br from-orange-500 to-orange-600 text-white p-6 rounded-2xl shadow-lg">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-orange-100 text-sm font-medium">Pending Approvals</p>
                            <p className="text-3xl font-bold">{stats.pendingApprovals}</p>
                        </div>
                        <AlertCircle className="w-8 h-8 text-orange-200" />
                    </div>
                </div>

                <div className="bg-gradient-to-br from-emerald-500 to-emerald-600 text-white p-6 rounded-2xl shadow-lg">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-emerald-100 text-sm font-medium">Total Revenue</p>
                            <p className="text-3xl font-bold">${(stats.totalRevenue / 1000000).toFixed(1)}M</p>
                        </div>
                        <DollarSign className="w-8 h-8 text-emerald-200" />
                    </div>
                </div>
            </div>

            {/* Charts Section */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                {/* Auction Status Chart */}
                <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-6">
                    <h4 className="text-xl font-semibold text-gray-900 mb-6">Auction Status Distribution</h4>
                    <div className="space-y-4">
                        {['draft', 'active', 'ended', 'sold'].map(status => {
                            const count = auctions.filter(a => a.status === status).length;
                            const percentage = stats.totalAuctions > 0 ? (count / stats.totalAuctions) * 100 : 0;
                            const colors = {
                                draft: 'bg-yellow-500',
                                active: 'bg-green-500',
                                ended: 'bg-red-500',
                                sold: 'bg-blue-500'
                            };

                            return (
                                <div key={status} className="flex items-center gap-4">
                                    <div className="flex-1">
                                        <div className="flex justify-between items-center mb-1">
                                            <span className="text-sm font-medium text-gray-700 capitalize">{status}</span>
                                            <span className="text-sm text-gray-500">{count} auctions</span>
                                        </div>
                                        <div className="w-full bg-gray-200 rounded-full h-2">
                                            <div
                                                className={`${colors[status]} h-2 rounded-full transition-all duration-500`}
                                                style={{ width: `${percentage}%` }}
                                            ></div>
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>

                {/* Revenue Chart */}
                <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-6">
                    <h4 className="text-xl font-semibold text-gray-900 mb-6">Revenue Overview</h4>
                    <div className="space-y-6">
                        <div className="text-center">
                            <div className="text-3xl font-bold text-emerald-600 mb-2">
                                ${(stats.totalRevenue / 1000000).toFixed(1)}M
                            </div>
                            <div className="text-sm text-gray-600">Total Revenue</div>
                        </div>

                        <div className="grid grid-cols-2 gap-4">
                            <div className="text-center p-4 bg-blue-50 rounded-xl">
                                <div className="text-2xl font-bold text-blue-600">
                                    {purchases.filter(p => p.status === 'approved').length}
                                </div>
                                <div className="text-sm text-gray-600">Approved Sales</div>
                            </div>
                            <div className="text-center p-4 bg-orange-50 rounded-xl">
                                <div className="text-2xl font-bold text-orange-600">
                                    {purchases.filter(p => p.status === 'pending_approval').length}
                                </div>
                                <div className="text-sm text-gray-600">Pending Sales</div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Activity Timeline */}
            <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-6">
                <h4 className="text-xl font-semibold text-gray-900 mb-6">Recent Activity</h4>
                <div className="space-y-4">
                    {[...bids, ...purchases]
                        .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
                        .slice(0, 10)
                        .map((item, index) => (
                            <div key={`${item._id}-${index}`} className="flex items-center gap-4 p-3 bg-gray-50 rounded-lg">
                                <div className={`w-3 h-3 rounded-full ${item.status === 'approved' ? 'bg-green-500' :
                                    item.status === 'pending' || item.status === 'pending_approval' ? 'bg-orange-500' :
                                        item.status === 'rejected' ? 'bg-red-500' :
                                            'bg-blue-500'
                                    }`}></div>
                                <div className="flex-1">
                                    <div className="text-sm font-medium text-gray-900">
                                        {item.bidAmount ? `Bid of $${item.bidAmount.toLocaleString()}` : `Purchase of $${item.purchaseAmount.toLocaleString()}`}
                                    </div>
                                    <div className="text-xs text-gray-500">
                                        {new Date(item.createdAt).toLocaleDateString()} • {item.status}
                                    </div>
                                </div>
                            </div>
                        ))}
                </div>
            </div>

            {/* Recent Auctions */}
            <div className="bg-white rounded-2xl shadow-lg border border-gray-100">
                <div className="p-6 border-b border-gray-100">
                    <div className="flex justify-between items-center">
                        <h4 className="text-xl font-semibold text-gray-900">Recent Auctions</h4>
                        <button
                            onClick={() => setActiveTab('auctions')}
                            className="text-blue-600 hover:text-blue-700 font-medium"
                        >
                            View All →
                        </button>
                    </div>
                </div>
                <div className="p-6">
                    {loading ? (
                        <div className="flex justify-center py-8">
                            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                        </div>
                    ) : auctions.length === 0 ? (
                        <div className="text-center py-12">
                            <Gavel className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                            <h3 className="text-lg font-medium text-gray-900 mb-2">No auctions yet</h3>
                            <p className="text-gray-500 mb-4">Create your first auction to get started</p>
                            <button
                                onClick={() => setShowCreateForm(true)}
                                className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700"
                            >
                                Create Auction
                            </button>
                        </div>
                    ) : (
                        <div className="space-y-4">
                            {auctions.slice(0, 5).map(auction => (
                                <div key={auction._id} className="flex items-center justify-between p-4 bg-gray-50 rounded-xl hover:bg-gray-100 transition-colors">
                                    <div className="flex items-center space-x-4">
                                        <div className={`w-3 h-3 rounded-full ${auction.status === 'active' ? 'bg-green-500' :
                                            auction.status === 'draft' ? 'bg-yellow-500' :
                                                auction.status === 'ended' ? 'bg-red-500' :
                                                    'bg-gray-500'
                                            }`}></div>
                                        <div>
                                            <h5 className="font-semibold text-gray-900">{auction.title}</h5>
                                            <p className="text-sm text-gray-600">{auction.description}</p>
                                        </div>
                                    </div>
                                    <div className="flex items-center space-x-4 text-sm text-gray-600">
                                        <span className="flex items-center gap-1">
                                            <Users className="w-4 h-4" />
                                            {getAuctionBidCount(auction._id)} bids
                                        </span>
                                        <span className="flex items-center gap-1">
                                            <DollarSign className="w-4 h-4" />
                                            ${getAuctionCurrentPrice(auction).toLocaleString()}
                                        </span>
                                        <button
                                            onClick={() => {
                                                setSelectedAuction(auction._id);
                                                setActiveTab('details');
                                                fetchAuctionDetails(auction._id);
                                            }}
                                            className="text-blue-600 hover:text-blue-700 font-medium"
                                        >
                                            View Details
                                        </button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );

    if (loading) {
        return (
            <div className="flex justify-center items-center h-64">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            {/* Tabs */}
            <div className="border-b">
                <nav className="flex space-x-3">
                    <button
                        onClick={() => setActiveTab('overview')}
                        className={`py-2 px-5 border-b-2 font-medium text-sm ${activeTab === 'overview'
                            ? 'border-blue-500 text-blue-600'
                            : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                            }`}
                    >
                        Overview
                    </button>
                    <button
                        onClick={() => setActiveTab('auctions')}
                        className={`py-2 px-5 border-b-2 font-medium text-sm ${activeTab === 'auctions'
                            ? 'border-blue-500 text-blue-600'
                            : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                            }`}
                    >
                        Auctions
                    </button>
                    <button
                        onClick={() => setActiveTab('details')}
                        className={`py-2 px-1 border-b-2 font-medium text-sm ${activeTab === 'details'
                            ? 'border-blue-500 text-blue-600'
                            : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                            }`}
                    >
                        Action
                    </button>
                </nav>
            </div>

            {/* Tab Content */}
            {activeTab === 'overview' && renderOverviewTab()}
            {activeTab === 'auctions' && renderAuctionsTab()}
            {activeTab === 'details' && renderDetailsTab()}

            {/* Create Auction Modal */}
            {showCreateForm && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full mx-4 max-h-[90vh] overflow-y-auto">
                        <div className="p-6 border-b border-gray-200">
                            <div className="flex justify-between items-center">
                                <h3 className="text-2xl font-bold text-gray-900">Create New Auction</h3>
                                <button
                                    onClick={() => {
                                        setShowCreateForm(false);
                                        resetForm();
                                    }}
                                    className="text-gray-400 hover:text-gray-600"
                                >
                                    <XCircle className="w-6 h-6" />
                                </button>
                            </div>
                        </div>
                        <div className="p-6">
                            <form onSubmit={handleCreateAuction} className="space-y-6">
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                    <div className="md:col-span-2">
                                        <label className="block text-sm font-medium text-gray-700 mb-2">Title</label>
                                        <input
                                            type="text"
                                            value={formData.title}
                                            onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                                            className="w-full p-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                            placeholder="e.g., Pahlavi Dynasty Historical Archive"
                                            required
                                        />
                                    </div>

                                    <div className="md:col-span-2">
                                        <label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
                                        <textarea
                                            value={formData.description}
                                            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                                            rows="3"
                                            className="w-full p-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                            placeholder="Brief description for admin reference"
                                            required
                                        />
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">Minimum Bid ($)</label>
                                        <input
                                            type="number"
                                            value={formData.minimumBid}
                                            onChange={(e) => setFormData({ ...formData, minimumBid: parseInt(e.target.value) })}
                                            className="w-full p-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                            min="1"
                                            required
                                        />
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">Maximum Bid / Buy Now Price ($)</label>
                                        <input
                                            type="number"
                                            value={formData.maximumBid}
                                            onChange={(e) => setFormData({ ...formData, maximumBid: parseInt(e.target.value) })}
                                            className="w-full p-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                            min="1"
                                            required
                                        />
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">Bid Increment ($)</label>
                                        <input
                                            type="number"
                                            value={formData.bidIncrement}
                                            onChange={(e) => setFormData({ ...formData, bidIncrement: parseInt(e.target.value) })}
                                            className="w-full p-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                            min="1000"
                                            step="1000"
                                            required
                                        />
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
                                        <select
                                            value={formData.status}
                                            onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                                            className="w-full p-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                        >
                                            <option value="draft">Draft</option>
                                            <option value="active">Active</option>
                                            <option value="ended">Ended</option>
                                            <option value="sold">Sold</option>
                                        </select>
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">Start Date</label>
                                        <input
                                            type="datetime-local"
                                            value={formData.startDate}
                                            onChange={(e) => setFormData({ ...formData, startDate: e.target.value })}
                                            className="w-full p-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                            required
                                        />
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">End Date</label>
                                        <input
                                            type="datetime-local"
                                            value={formData.endDate}
                                            onChange={(e) => setFormData({ ...formData, endDate: e.target.value })}
                                            className="w-full p-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                            required
                                        />
                                    </div>

                                    <div className="md:col-span-2">
                                        <label className="flex items-center gap-2">
                                            <input
                                                type="checkbox"
                                                checked={formData.adminApprovalRequired}
                                                onChange={(e) => setFormData({ ...formData, adminApprovalRequired: e.target.checked })}
                                                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                                            />
                                            <span className="text-sm font-medium text-gray-700">Require admin approval for bids and purchases</span>
                                        </label>
                                    </div>



                                    <div className="md:col-span-2">
                                        <label className="block text-sm font-medium text-gray-700 mb-2">Rich Text Content (HTML)</label>
                                        <div className="flex gap-2 mb-4">
                                            <button
                                                type="button"
                                                onClick={loadSimpleTemplate}
                                                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-medium"
                                            >
                                                Load Simple Template
                                            </button>
                                            <button
                                                type="button"
                                                onClick={() => setFormData({ ...formData, richTextContent: '' })}
                                                className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 text-sm font-medium"
                                            >
                                                Clear Content
                                            </button>
                                        </div>
                                        <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-4 mb-4">
                                            <p className="text-yellow-800 text-sm mb-2">
                                                <strong>Important:</strong> Use these button IDs in your HTML to integrate with the auction system:
                                            </p>
                                            <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-xs">
                                                <code className="bg-white px-2 py-1 rounded">id="auction-bid-btn"</code>
                                                <code className="bg-white px-2 py-1 rounded">id="auction-buynow-btn"</code>
                                                <code className="bg-white px-2 py-1 rounded">id="auction-bid-btn-2"</code>
                                                <code className="bg-white px-2 py-1 rounded">id="auction-buynow-btn-2"</code>
                                            </div>
                                        </div>
                                        <textarea
                                            value={formData.richTextContent}
                                            onChange={(e) => setFormData({ ...formData, richTextContent: e.target.value })}
                                            rows="15"
                                            className="w-full p-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent font-mono text-sm"
                                            placeholder="Enter your HTML content here. Include buttons with the specified IDs for auction functionality."
                                            required
                                        />
                                        <div className="mt-2 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                                            <p className="text-blue-800 text-sm mb-2">
                                                <strong>How to use custom HTML:</strong>
                                            </p>
                                            <ul className="text-blue-700 text-xs space-y-1">
                                                <li>• Click "Load Simple Template" to start with a basic template</li>
                                                <li>• Modify the HTML content as needed for your design</li>
                                                <li>• Keep the button IDs (auction-bid-btn, auction-buynow-btn) for functionality</li>
                                                <li>• You can add multiple buttons with different IDs (e.g., auction-bid-btn-2)</li>
                                                <li>• The system will automatically add the necessary JavaScript for auction functionality</li>
                                            </ul>
                                        </div>
                                    </div>
                                </div>

                                <div className="flex gap-4 pt-6 border-t border-gray-200">
                                    <button
                                        type="submit"
                                        className="bg-gradient-to-r from-blue-600 to-blue-700 text-white px-8 py-3 rounded-xl hover:from-blue-700 hover:to-blue-800 transition-all duration-200 flex items-center gap-2"
                                    >
                                        <Plus className="w-5 h-5" />
                                        Create Auction
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => {
                                            setShowCreateForm(false);
                                            resetForm();
                                        }}
                                        className="bg-gray-300 text-gray-700 px-6 py-3 rounded-xl hover:bg-gray-400 transition-colors"
                                    >
                                        Cancel
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            )}

            {/* Edit Auction Modal */}
            {showEditForm && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full mx-4 max-h-[90vh] overflow-y-auto">
                        <div className="p-6 border-b border-gray-200">
                            <div className="flex justify-between items-center">
                                <h3 className="text-2xl font-bold text-gray-900">Edit Auction</h3>
                                <button
                                    onClick={() => {
                                        setShowEditForm(false);
                                        setSelectedAuction(null);
                                        resetForm();
                                    }}
                                    className="text-gray-400 hover:text-gray-600"
                                >
                                    <XCircle className="w-6 h-6" />
                                </button>
                            </div>
                        </div>
                        <div className="p-6">
                            <form onSubmit={handleUpdateAuction} className="space-y-6">
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                    <div className="md:col-span-2">
                                        <label className="block text-sm font-medium text-gray-700 mb-2">Title</label>
                                        <input
                                            type="text"
                                            value={formData.title}
                                            onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                                            className="w-full p-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                            placeholder="e.g., Pahlavi Dynasty Historical Archive"
                                            required
                                        />
                                    </div>

                                    <div className="md:col-span-2">
                                        <label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
                                        <textarea
                                            value={formData.description}
                                            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                                            rows="3"
                                            className="w-full p-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                            placeholder="Brief description for admin reference"
                                            required
                                        />
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">Minimum Bid ($)</label>
                                        <input
                                            type="number"
                                            value={formData.minimumBid}
                                            onChange={(e) => setFormData({ ...formData, minimumBid: parseInt(e.target.value) })}
                                            className="w-full p-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                            min="1"
                                            required
                                        />
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">Maximum Bid / Buy Now Price ($)</label>
                                        <input
                                            type="number"
                                            value={formData.maximumBid}
                                            onChange={(e) => setFormData({ ...formData, maximumBid: parseInt(e.target.value) })}
                                            className="w-full p-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                            min="1"
                                            required
                                        />
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">Bid Increment ($)</label>
                                        <input
                                            type="number"
                                            value={formData.bidIncrement}
                                            onChange={(e) => setFormData({ ...formData, bidIncrement: parseInt(e.target.value) })}
                                            className="w-full p-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                            min="1000"
                                            step="1000"
                                            required
                                        />
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
                                        <select
                                            value={formData.status}
                                            onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                                            className="w-full p-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                        >
                                            <option value="draft">Draft</option>
                                            <option value="active">Active</option>
                                            <option value="ended">Ended</option>
                                            <option value="sold">Sold</option>
                                        </select>
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">Start Date</label>
                                        <input
                                            type="datetime-local"
                                            value={formData.startDate}
                                            onChange={(e) => setFormData({ ...formData, startDate: e.target.value })}
                                            className="w-full p-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                            required
                                        />
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">End Date</label>
                                        <input
                                            type="datetime-local"
                                            value={formData.endDate}
                                            onChange={(e) => setFormData({ ...formData, endDate: e.target.value })}
                                            className="w-full p-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                            required
                                        />
                                    </div>

                                    <div className="md:col-span-2">
                                        <label className="flex items-center gap-2">
                                            <input
                                                type="checkbox"
                                                checked={formData.adminApprovalRequired}
                                                onChange={(e) => setFormData({ ...formData, adminApprovalRequired: e.target.checked })}
                                                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                                            />
                                            <span className="text-sm font-medium text-gray-700">Require admin approval for bids and purchases</span>
                                        </label>
                                    </div>



                                    <div className="md:col-span-2">
                                        <label className="block text-sm font-medium text-gray-700 mb-2">Rich Text Content (HTML)</label>
                                        <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-4 mb-4">
                                            <p className="text-yellow-800 text-sm mb-2">
                                                <strong>Important:</strong> Use these button IDs in your HTML to integrate with the auction system:
                                            </p>
                                            <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-xs">
                                                <code className="bg-white px-2 py-1 rounded">id="auction-bid-btn"</code>
                                                <code className="bg-white px-2 py-1 rounded">id="auction-buynow-btn"</code>
                                                <code className="bg-white px-2 py-1 rounded">id="auction-bid-btn-2"</code>
                                                <code className="bg-white px-2 py-1 rounded">id="auction-buynow-btn-2"</code>
                                            </div>
                                        </div>
                                        <textarea
                                            value={formData.richTextContent}
                                            onChange={(e) => setFormData({ ...formData, richTextContent: e.target.value })}
                                            rows="15"
                                            className="w-full p-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent font-mono text-sm"
                                            placeholder="Enter your HTML content here. Include buttons with the specified IDs for auction functionality."
                                            required
                                        />
                                        <p className="text-xs text-gray-500 mt-1">
                                            Enter the complete HTML content for your auction page. Make sure to include buttons with the specified IDs.
                                        </p>
                                    </div>
                                </div>

                                <div className="flex gap-4 pt-6 border-t border-gray-200">
                                    <button
                                        type="submit"
                                        className="bg-gradient-to-r from-green-600 to-green-700 text-white px-8 py-3 rounded-xl hover:from-green-700 hover:to-green-800 transition-all duration-200 flex items-center gap-2"
                                    >
                                        <CheckCircle className="w-5 h-5" />
                                        Update Auction
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => {
                                            setShowEditForm(false);
                                            setSelectedAuction(null);
                                            resetForm();
                                        }}
                                        className="bg-gray-300 text-gray-700 px-6 py-3 rounded-xl hover:bg-gray-400 transition-colors"
                                    >
                                        Cancel
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default AuctionManagement;